import { useSyncExternalStore } from 'react'
import {
  CONSENT_VERSION,
  ConsentState,
  clearConsent,
  loadConsent,
  saveConsent,
} from './state'
import { useGeo } from './useGeo'

// Module-level store. Plain useSyncExternalStore — Zustand isn't in the project
// and a single banner doesn't justify a new dependency.

interface StoreSnapshot {
  consent: ConsentState | null
  // forceShow lets the Profile "Cookies & Privacy" row (Task 6) re-open the
  // banner even outside the consent region. Stored in memory only.
  forceShow: boolean
}

let snapshot: StoreSnapshot = {
  consent: typeof window === 'undefined' ? null : loadConsent(),
  forceShow: false,
}

const listeners = new Set<() => void>()

function setSnapshot(next: StoreSnapshot) {
  snapshot = next
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot() {
  return snapshot
}

function commit(partial: { ads: boolean; replay: boolean }, region: string) {
  const next: ConsentState = {
    ads: partial.ads,
    replay: partial.replay,
    timestamp: Date.now(),
    region,
    version: CONSENT_VERSION,
  }
  saveConsent(next)
  setSnapshot({ consent: next, forceShow: false })
}

export function useConsent() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const geo = useGeo()

  const hasDecided = state.consent !== null
  const shouldShowBanner =
    state.forceShow || (geo.inConsentRegion && !hasDecided)

  return {
    consent: state.consent,
    hasDecided,
    inConsentRegion: geo.inConsentRegion,
    country: geo.country,
    shouldShowBanner,
    accept() {
      commit({ ads: true, replay: true }, geo.country)
    },
    reject() {
      commit({ ads: false, replay: false }, geo.country)
    },
    customize(partial: { ads?: boolean; replay?: boolean }) {
      const base = state.consent ?? { ads: false, replay: false }
      commit(
        {
          ads: partial.ads ?? base.ads,
          replay: partial.replay ?? base.replay,
        },
        geo.country,
      )
    },
    revoke() {
      clearConsent()
      setSnapshot({ consent: null, forceShow: true })
    },
    requestReopen() {
      setSnapshot({ ...snapshot, forceShow: true })
    },
    dismissForceShow() {
      if (snapshot.forceShow) {
        setSnapshot({ ...snapshot, forceShow: false })
      }
    },
  }
}
